import { notifyError, notifySuccess } from "@/lib/notification";
import * as internshipService from "@/services/internship.service";
import {
  InternshipCreateType,
  InternshipParamsType,
  InternshipType,
} from "@/services/internship.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const internshipsKey = {
  all: ["internships"] as const,
  lists: (filters: InternshipParamsType) =>
    [...internshipsKey.all, "lists", filters] as const,
  detail: (id: string) => [...internshipsKey.all, "detail", id] as const,
};

export const useInternshipsQuery = (filters: InternshipParamsType) =>
  useQuery({
    queryKey: internshipsKey.lists(filters),
    queryFn: () => internshipService.fetchAllInternships(filters),
    placeholderData: (prev) => prev,
  });

export const useInternshipByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: internshipsKey.detail(id),
    queryFn: () => internshipService.fetchInternshipById(id),
    enabled: enabled && !!id,
    // Deleted postings resolve to null (a cached success), so anything that
    // still throws is transient — worth one retry, except auth failures.
    retry: (failureCount, error) => {
      const status = (error as AxiosError)?.response?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 1;
    },
  });

export const useInternshipApplicationsQuery = (
  id: string,
  params?: { page?: number; limit?: number; status?: string },
  enabled = true
) =>
  useQuery({
    queryKey: [...internshipsKey.detail(id), "applications", params],
    queryFn: () => internshipService.fetchInternshipApplications(id, params),
    enabled: enabled && !!id,
  });

export const useUpdateApplicationStatusMutation = (internshipId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, status }: { applicationId: string; status: string }) =>
      internshipService.updateApplicationStatus(applicationId, status),
    onSuccess: () => {
      // Invalidate the applications list for this internship
      queryClient.invalidateQueries({
        queryKey: [...internshipsKey.detail(internshipId), "applications"],
      });
      notifySuccess("Application status updated successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to update application status"
      ),
  });
};

export const useCreateInternshipMutation = (filters: InternshipParamsType) => {
  const queryClient = useQueryClient();

  return useMutation<InternshipType, AxiosError, InternshipCreateType>({
    mutationFn: (data) => internshipService.createInternship(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internshipsKey.lists(filters),
      });
      notifySuccess("Internship added successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to add internship"),
  });
};

export const useUpdateInternshipMutation = (filters: InternshipParamsType) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ _id, data }: { _id: string; data: InternshipCreateType }) =>
      internshipService.updateInternship(_id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: internshipsKey.lists(filters),
      });
      queryClient.invalidateQueries({
        queryKey: internshipsKey.detail(variables._id),
      });
      notifySuccess("Internship updated successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to update internship"
      ),
  });
};


export const usePublishInternshipMutation = (filters: InternshipParamsType) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => internshipService.publishInternship(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internshipsKey.lists(filters),
      });
      notifySuccess("Internship published successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to publish"),
  });
};


export const useCloseInternshipMutation = (filters: InternshipParamsType) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => internshipService.closeInternship(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internshipsKey.lists(filters),
      });
      notifySuccess("Internship closed successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to close"),
  });
};

export const useDeleteInternshipMutation = (filters: InternshipParamsType) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (_id: string) => internshipService.deleteInternship(_id),
    onSuccess: (_, _id) => {
      queryClient.invalidateQueries({
        queryKey: internshipsKey.lists(filters),
      });
      queryClient.removeQueries({
        queryKey: internshipsKey.detail(_id),
      });
      notifySuccess("Internship deleted successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to delete internship"
      ),
  });
};

// Student-facing interview hooks live in interview.queries.ts — read-only.

// ─── Company Discovery Hooks ──────────────────────────────────────────

export const companyKey = {
  all: ["companies"] as const,
  lists: (params: any) => [...companyKey.all, "lists", params] as const,
  detail: (id: string) => [...companyKey.all, "detail", id] as const,
  internships: (id: string, params: any) => [...companyKey.detail(id), "internships", params] as const,
};

export const useAllCompaniesQuery = (params?: { page?: number; limit?: number }) =>
  useQuery({
    queryKey: companyKey.lists(params),
    queryFn: () => internshipService.fetchAllCompanies(params),
    placeholderData: (prev) => prev,
  });

export const useCompanyProfileQuery = (recruiterId: string, enabled = true) =>
  useQuery({
    queryKey: companyKey.detail(recruiterId),
    queryFn: () => internshipService.fetchCompanyProfile(recruiterId),
    enabled: enabled && !!recruiterId && recruiterId !== "undefined",
  });

export const useCompanyInternshipsQuery = (
  recruiterId: string,
  params?: { page?: number; limit?: number },
  enabled = true
) =>
  useQuery({
    queryKey: companyKey.internships(recruiterId, params),
    queryFn: () => internshipService.fetchCompanyInternships(recruiterId, params),
    enabled: enabled && !!recruiterId && recruiterId !== "undefined",
    placeholderData: (prev) => prev,
  });

/** Scoped to the caller's token — no student id is passed or needed. */
export const useActiveInternshipQuery = (enabled = true) =>
  useQuery({
    queryKey: ["active-internship"],
    queryFn: internshipService.fetchActiveInternship,
    enabled,
  });

export const useDashboardAnalyticsQuery = () =>
  useQuery({
    queryKey: ["student", "dashboard", "analytics"],
    queryFn: () => internshipService.fetchDashboardAnalytics(),
  });
